import { useEffect, useState } from "react";
import AppLayoutMFE from "./components/AppLayoutMFE/AppLayoutMFE";
import LoginPageInternal from "./pages/Landingpage/Landingpage";
import { Spinner } from "@cloudscape-design/components";
import { getUser } from "./services/Auth/auth";
import { SpaceBetween } from "@cloudscape-design/components";
import { Hub } from "aws-amplify/utils";
import { getCurrentUser } from "aws-amplify/auth";
import { SplitPanelProvider } from "./SplitPanelContext";
import customTheme from "./customTheme";
import "@cloudscape-design/global-styles/index.css";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import { applyTheme } from "@cloudscape-design/components/theming";
import { ChatSessionProvider } from "./components/Agent/ChatContext";
import { ThemeProvider } from "./components/ThemeContext";
import AppRefreshManager from "./AppRefreshManager";
import { SidebarProvider, SidebarInset } from "./components/ui/sidebar";
import { AppSidebar } from "./components/Sidebar";

const App = () => {
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);

  const [colorMode, setColorMode] = useState(() => {
    const savedMode = localStorage.getItem("colorMode");
    return savedMode || "system";
  });

  // Make effectiveTheme a state variable
  const [effectiveTheme, setEffectiveTheme] = useState(() => {
    const getSystemTheme = () => {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    };
    const getEffectiveTheme = (mode) => {
      if (mode === "system") {
        return getSystemTheme();
      }
      return mode;
    };
    return getEffectiveTheme(colorMode);
  });

  const getSystemTheme = () => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  };

  const getEffectiveTheme = (mode) => {
    if (mode === "system") {
      return getSystemTheme();
    }
    return mode;
  };

  const setThemeMode = (mode) => {
    const validModes = ["SYSTEM", "LIGHT", "DARK"];
    const normalizedMode = mode.toUpperCase();

    if (validModes.includes(normalizedMode)) {
      setColorMode(normalizedMode.toLowerCase());
    } else {
      console.warn(`Invalid theme mode: ${mode}. Valid options are: SYSTEM, LIGHT, DARK`);
    }
  };

  useEffect(() => {
    // Listen for auth events from Amplify Hub FIRST
    // This must be set up before checking auth state to catch OAuth callbacks
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      console.log("Auth event:", payload.event, payload.data);
      switch (payload.event) {
        case "signInWithRedirect":
          // OAuth sign-in completed successfully
          console.log("SSO sign-in completed, checking auth state...");
          // Clear the URL parameters after successful auth
          window.history.replaceState({}, document.title, window.location.pathname);
          checkAuthState();
          break;
        case "signInWithRedirect_failure":
          // OAuth sign-in failed
          console.error("SSO sign-in failed:", payload.data);
          // Clear the URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          setLoading(false);
          break;
        case "signedIn":
          // User signed in (any method)
          console.log("User signed in");
          checkAuthState();
          break;
        case "signedOut":
          // User signed out
          console.log("User signed out");
          setAuthUser(null);
          break;
        case "tokenRefresh":
          console.log("Token refreshed");
          break;
        case "tokenRefresh_failure":
          console.error("Token refresh failed");
          setAuthUser(null);
          break;
        case "customOAuthState":
          console.log("Custom OAuth state:", payload.data);
          break;
        default:
          console.log("Unhandled auth event:", payload.event);
          break;
      }
    });

    // Check if this is an OAuth callback (has ?code= in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');
    
    if (hasAuthCode) {
      console.log("OAuth callback detected with code, waiting for Amplify to process...");
      // Give Amplify time to process the OAuth callback
      // If no Hub event fires within 5 seconds, check auth state anyway
      const timeout = setTimeout(() => {
        console.log("OAuth processing timeout - checking auth state");
        checkAuthState();
      }, 5000);
      
      return () => {
        clearTimeout(timeout);
        unsubscribe();
      };
    } else {
      // No OAuth callback - check auth state normally
      checkAuthState();
    }

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const newEffectiveTheme = getEffectiveTheme(colorMode);
    setEffectiveTheme(newEffectiveTheme);
    applyMode(newEffectiveTheme === "light" ? Mode.Light : Mode.Dark);
    localStorage.setItem("colorMode", colorMode);

    // Apply dark class to document for shadcn/ui sidebar theming
    if (newEffectiveTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = () => {
      if (colorMode === "system") {
        const updatedEffectiveTheme = getEffectiveTheme(colorMode);
        setEffectiveTheme(updatedEffectiveTheme); // Update the state
        applyMode(updatedEffectiveTheme === "light" ? Mode.Light : Mode.Dark);

        // Update dark class for shadcn/ui sidebar theming
        if (updatedEffectiveTheme === "dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      }
    };

    if (colorMode === "system") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    }

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [colorMode]);

  useEffect(() => {
    applyTheme({ theme: customTheme });
  }, []);

  const checkAuthState = async () => {
    setLoading(true);
    try {
      const user = await getUser();
      setAuthUser(user);
    } catch (error) {
      console.log(error);
      setAuthUser(null);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  return (
    <div>
      <ThemeProvider
        colorMode={colorMode}
        effectiveTheme={effectiveTheme}
        setThemeMode={setThemeMode}
      >
        {loading ? (
          <SpaceBetween alignItems="center">
            <div style={{ marginTop: "20px" }}>
              <Spinner size="large" />
            </div>
          </SpaceBetween>
        ) : authUser ? (
          <AppRefreshManager>
            <ChatSessionProvider>
              <SplitPanelProvider>
                <SidebarProvider defaultOpen={false}>
                  <AppSidebar
                    user={authUser}
                    colorMode={colorMode}
                    effectiveTheme={effectiveTheme}
                    setThemeMode={setThemeMode}
                    setAuthUser={checkAuthState}
                  />
                  <SidebarInset>
                    <AppLayoutMFE
                      user={authUser}
                      colorMode={colorMode}
                      setThemeMode={setThemeMode}
                    />
                  </SidebarInset>
                </SidebarProvider>
              </SplitPanelProvider>
            </ChatSessionProvider>
          </AppRefreshManager>
        ) : (
          <LoginPageInternal setAuthUser={checkAuthState} />
        )}
      </ThemeProvider>
    </div>
  );
};

export default App;
